import type {
	BasketRowResponse,
	BonusCheck,
	BonusInformation,
	CampaignProductsResponse,
	Cart,
	CartRequest,
	CommunicationPreferences,
	CreateCustomizationRequest,
	CreditSimulation,
	Customer,
	CustomerContactId,
	CustomerInformation,
	CustomerServiceAvailabilityResponse,
	DeliveryMethodItem,
	FaqSearchItem,
	InvoiceResponse,
	JulaProCustomerContact,
	JulaProCustomerUpdateRequest,
	MinimizedAddToCartResponse,
	MinimizedAddToWishlistResponse,
	MinimizedCartResponse,
	MinimizedWishlistResponse,
	OrderDetail,
	OrdersResponse,
	Organization,
	PaymentRequest,
	PaymentResult,
	PermissionResponse,
	PermissionType,
	ProductArchiveFacetType,
	ProductArchiveSearchResponse,
	ProductArchiveSortOption,
	ProductCustomizationResponse,
	ProductList,
	ProductQuestionsResponse,
	Quotation,
	RelatedProductsResponse,
	RelationType,
	ResursBankPaymentSignage,
	ReviewResponse,
	ReviewScore,
	ReviewSorting,
	SearchResults,
	SimilarProductsResponse,
	StorageType,
	Store,
	StoredFileResponse,
	StoreStockCard,
	Ticket,
	VariantMetaData,
	VariantQty,
	VariantQtyBase,
	VariantResponse,
	VariantStockResponse,
	VerifyPaymentRequest,
	Wishlist,
	Worksite,
} from 'models/api';
import type { ProductId, VariantId } from 'models/product';
import type { ProductCard } from 'models/productCard';
import type {
	CampaignId,
	CartId,
	CategoryId,
	CustomizationId,
	JSONObject,
	PaymentProviderMethodId,
	QuotationIdString,
	StoreId,
	TransactionId,
} from 'types';

import type { EndpointDefinitions } from '../helpers';

type ContactPath = 'Customer/julapro/contact/{{customerContactId}}';

// An interface has no implicit index signature, so it wouldn't satisfy the
// pathParams record in EndpointSpec.
// oxlint-disable-next-line typescript/consistent-type-definitions
type ContactPathParams = {
	customerContactId: CustomerContactId;
};

interface ProductArchiveFacetSelection {
	facetType: ProductArchiveFacetType;
	selected: string[];
}

type SearchSortOption =
	| 'News'
	| 'None'
	| 'Popularity'
	| 'PriceHighestFirst'
	| 'PriceLowestFirst'
	| 'ProductTitleAscending'
	| 'ProductTitleDescending'
	| 'Relevance'
	| 'ReviewScore';

interface FacetSelection {
	facetType: string;
	selected: string[];
}

type SortOptionValue =
	| 'News'
	| 'None'
	| 'Popularity'
	| 'PriceHighestFirst'
	| 'PriceLowestFirst'
	| 'ProductTitleAscending'
	| 'ProductTitleDescending'
	| 'Relevance'
	| 'ReviewScore';

interface BasicSearchParameters {
	offset: number;
	pageSize: number;
}

interface AdvancedSearchParameters extends BasicSearchParameters {
	facetFilter?: FacetSelection[];
	// Not nullable in type, but we've never sent it so there must be a default set
	sortOption?: SortOptionValue[];
}

interface AdvancedSearchRequest {
	brands?: BasicSearchParameters;
	categories?: BasicSearchParameters;
	directSearch?: boolean;
	inspirationPages?: BasicSearchParameters;
	products: AdvancedSearchParameters;
	searchTerm: string;
	stores?: BasicSearchParameters;
}

type TicketType = 'Customer';

/** Digital Platform API endpoints. */
export interface DpApiEndpoints {
	DeleteContact: {
		path: ContactPath;
		pathParams: ContactPathParams;
		response: undefined;
	};
	DeleteWishlist: {
		path: 'Wishlist';
		response: undefined;
	};
	DeleteWishlistVariant: {
		path: 'Wishlist/variants/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		queryParams: {
			storeId?: StoreId;
		};
		response: Wishlist;
	};
	GetBundleVariants: {
		path: 'Bundle/{{bundleId}}/variants';
		pathParams: {
			bundleId: VariantId;
		};
		queryParams: {
			storeId?: StoreId;
		};
		response: BasketRowResponse[];
	};
	GetCampaignProducts: {
		path: 'Campaign/{{campaignId}}';
		pathParams: {
			campaignId: CampaignId;
		};
		queryParams?: {
			offset?: string;
			pageSize?: number;
			storeId?: StoreId;
		};
		response: CampaignProductsResponse;
	};
	GetCart: {
		path: 'Cart/{{cartId}}';
		pathParams: {
			cartId: CartId;
		};
		response: Cart;
	};
	GetCartMini: {
		path: 'Cart/mini';
		response: MinimizedCartResponse;
	};
	GetCommunicationPreferences: {
		path: 'Customer/julaclub/communication-preferences';
		response: CommunicationPreferences;
	};
	GetContact: {
		path: ContactPath;
		pathParams: ContactPathParams;
		response: JulaProCustomerContact;
	};
	GetContacts: {
		path: 'Customer/julapro/contact';
		response: JulaProCustomerContact[];
	};
	GetCreditSimulation: {
		path: 'Customer/CreditSimulation';
		queryParams: {
			price: number;
		};
		response: CreditSimulation;
	};
	GetCustomer: {
		path: 'Customer';
		response: CustomerInformation;
	};
	GetCustomerAvailability: {
		path: 'CustomerService/availability';
		response: CustomerServiceAvailabilityResponse;
	};
	GetCustomerBonus: {
		path: 'Customer/bonus';
		response: BonusInformation;
	};
	GetCustomerBonusChecks: {
		path: 'Customer/bonusChecks';
		response: BonusCheck[];
	};
	GetCustomerInvoices: {
		path: 'Customer/invoice';
		queryParams?: {
			offset?: string;
			pageSize?: number;
		};
		response: InvoiceResponse;
	};
	GetCustomerOrder: {
		path: 'Customer/order/{{orderId}}';
		pathParams: {
			orderId: TransactionId;
		};
		response: OrderDetail;
	};
	GetCustomerOrders: {
		path: 'Customer/order';
		queryParams?: {
			offset?: string;
			pageSize?: number;
		};
		response: OrdersResponse;
	};
	GetCustomerOrderStatus: {
		path: 'Customer/{{customerId}}/order/{{orderId}}';
		pathParams: {
			customerId: string;
			orderId: TransactionId;
		};
		response: OrderDetail;
	};
	GetCustomerSearch: {
		path: 'Customer/search';
		queryParams: {
			email: string;
		};
		response: Customer;
	};
	GetDeliveryMethods: {
		path: 'DeliveryMethods/variant/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		queryParams: {
			postalCode: string;
			storeId?: StoreId;
		};
		response: DeliveryMethodItem[];
	};
	GetFaqSearch: {
		path: 'FaqSearch/{{searchString}}';
		pathParams: {
			searchString: string;
		};
		queryParams: {
			includeContent: 'false' | 'true';
			pageSize?: number;
		};
		response: FaqSearchItem[];
	};
	GetJulaProSearchCompany: {
		path: 'Customer/julapro/search/company';
		queryParams: {
			searchString: string;
		};
		response: Organization[];
	};
	GetJulaProWorksite: {
		path: 'Customer/julapro/worksite/{{worksiteId}}';
		pathParams: {
			worksiteId: string;
		};
		queryParams?: {
			customerNumber?: string;
		};
		response: Worksite;
	};
	GetNearbyStores: {
		path: 'Stores/nearbyStores';
		queryParams: {
			storeId: StoreId;
		};
		response: Store[];
	};
	GetOrder: {
		path: 'Customer/order';
		response: OrdersResponse;
	};
	GetPermissions: {
		path: 'Customer/permissions';
		queryParams: { permissions: PermissionType | PermissionType[] };
		response: PermissionResponse;
	};
	GetProductCustomization: {
		path: 'ProductCustomization/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		queryParams: {
			customizationId?: CustomizationId;
			showHistory?: 'false' | 'true';
		};
		response: ProductCustomizationResponse;
	};
	GetProductQuestions: {
		path: 'ProductQuestions/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		queryParams?: {
			offset?: number;
			pageSize?: number;
		};
		response: ProductQuestionsResponse;
	};
	GetQuotation: {
		path: 'Quotations/{{quotationId}}';
		pathParams: {
			quotationId: QuotationIdString;
		};
		response: Quotation;
	};
	GetQuotations: {
		path: 'Quotations';
		response: Quotation[];
	};
	GetRelatedProducts: {
		path: 'variant/{{variantId}}/relatedproducts';
		pathParams: {
			variantId: VariantId;
		};
		queryParams: {
			offset?: number;
			pageSize?: number;
			relationType: RelationType;
			returnStockData?: 'true';
			storeId?: StoreId;
		};
		response: RelatedProductsResponse;
	};
	GetResursPaymentPriceSignage: {
		path: 'payment/resursbank/price-signage';
		queryParams: {
			amount: number;
			paymentMethodIdProvider: PaymentProviderMethodId;
		};
		response: ResursBankPaymentSignage;
	};
	GetReviews: {
		path: 'Reviews/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		queryParams?: {
			filter?: 'All' | 'HasImage';
			offset?: string;
			pageSize?: number;
			returnReviewSummary?: 'true';
			reviewScore?: ReviewScore;
			reviewSorting?: ReviewSorting;
		};
		response: ReviewResponse;
	};
	GetSearchPopularProducts: {
		path: 'Search/popularproducts';
		queryParams: {
			categoryId?: string;
			limitToInStock?: 'false' | 'true';
			limitToSellableOnWeb?: 'false' | 'true';
			offset?: number;
			pageSize?: number;
		};
		response: ProductCard[];
	};
	GetSearchPopularTerms: {
		path: 'Search/popularterms';
		response: string[];
	};
	GetSharedWishlist: {
		path: 'Wishlist/{{wishlistId}}';
		pathParams: {
			wishlistId: string;
		};
		queryParams?: {
			storeId?: StoreId;
		};
		response: Wishlist;
	};
	GetSimilarProducts: {
		path: 'Product/{{productId}}/similar';
		pathParams: {
			productId: ProductId;
		};
		response: SimilarProductsResponse;
	};
	GetStore: {
		path: 'Stores/{{storeId}}';
		pathParams: {
			storeId: StoreId;
		};
		response: Store;
	};
	GetStores: {
		path: 'Stores';
		response: Store[];
	};
	GetTicket: {
		path: 'Ticket/{{ticketType}}';
		pathParams: {
			ticketType: TicketType;
		};
		response: Ticket;
	};
	GetUserFileStorage: {
		path: 'UserFileStorage/{{storageType}}';
		pathParams: {
			storageType: StorageType;
		};
		response: StoredFileResponse[];
	};
	GetVariant: {
		path: 'variant/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		response: VariantResponse;
	};
	GetVariantStock: {
		path: 'variantStock';
		queryParams: {
			returnShippingUsps?: 'true';
			storeId?: StoreId;
			variantIds: VariantId | VariantId[];
		};
		response: VariantStockResponse[];
	};
	GetVariantStockStores: {
		path: 'variantStock/{{variantId}}/stores';
		pathParams: { variantId: VariantId };
		queryParams:
			| {
					pageSize?: number;
					// if no store id 'Nearby' will return empty list
					storeId: StoreId;
					storeSorting: 'Nearby';
			  }
			| {
					pageSize?: number;
					storeId?: StoreId;
					storeSorting?: 'Alphabetical';
			  };
		response: StoreStockCard[];
	};
	GetWishlistMini: {
		path: 'Wishlist/mini';
		response: MinimizedWishlistResponse;
	};
	PostCart: {
		path: 'Cart';
		response: Cart;
	};
	PostCartFinalizePayment: {
		path: 'Cart/{{cartId}}/finalize';
		pathParams: {
			cartId: CartId;
		};
		response: Cart;
	};
	PostCartMiniVariant: {
		body?: VariantMetaData;
		path: 'Cart/mini/variants/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		queryParams: {
			customizationId?: CustomizationId;
			maxReturnedAccessories?: number;
			onlyInWebStock?: 'false' | 'true';
			onlySellableOnline?: 'false' | 'true';
			qty: number;
			returnAccessories?: 'false' | 'true';
			returnAddedProducts?: 'false' | 'true';
			setDistinctValue?: 'false' | 'true';
		};
		response: MinimizedAddToCartResponse;
	};
	PostCartMiniVariants: {
		body: VariantQty[];
		path: 'Cart/mini/variants';
		queryParams?: {
			maxReturnedAccessories?: number;
			onlyInWebStock?: 'false' | 'true';
			onlySellableOnline?: 'false' | 'true';
			returnAccessories?: 'false' | 'true';
			returnAddedProducts?: 'false' | 'true';
			setDistinctValue?: 'false' | 'true';
		};
		response: MinimizedAddToCartResponse;
	};
	PostCartPayment: {
		body: PaymentRequest;
		path: 'Cart/{{cartId}}/payment';
		pathParams: {
			cartId: CartId;
		};
		response: PaymentResult;
	};
	PostCartVariant: {
		body?: VariantMetaData;
		path: 'Cart/variants/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		queryParams: {
			customizationId?: CustomizationId;
			qty: number;
			setDistinctValue?: 'false' | 'true';
		};
		response: Cart;
	};
	PostCartVariants: {
		body: VariantQty[];
		path: 'Cart/variants';
		queryParams: {
			setDistinctValue?: 'false' | 'true';
		};
		response: Cart;
	};
	PostCheckoutCart: {
		body: CartRequest;
		path: 'Cart/checkout';
		response: Cart;
	};
	PostCommunicationPreferences: {
		body: CommunicationPreferences;
		path: 'Customer/julaclub/communication-preferences';
		response: CommunicationPreferences;
	};
	PostConsent: {
		body: {
			analytics: boolean;
			marketing: boolean;
			social: boolean;
			// yyyy-MM-dd
			validTo: string;
		};
		path: 'Customer/julaclub/consent';
		response: undefined;
	};
	PostCopyCart: {
		path: 'Cart/{{cartId}}/copy';
		pathParams: {
			cartId: CartId;
		};
		response: Cart;
	};
	PostFeedback: {
		path: 'Feedback';
		response: undefined;
	};
	PostProductCustomization: {
		body: CreateCustomizationRequest[];
		path: 'ProductCustomization/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		response: ProductCustomizationResponse;
	};
	PostProductList: {
		body: JSONObject;
		path: 'ProductList';
		response: ProductList;
	};
	PostQuotationPayment: {
		body: PaymentRequest;
		path: 'Quotations/{{quotationId}}/payment';
		pathParams: {
			quotationId: QuotationIdString;
		};
		response: PaymentResult;
	};
	PostSearch: {
		path: 'Search';
		queryParams: {
			directSearch?: 'false' | 'true';
			offset?: number;
			pageSize?: number;
			searchString: string;
			sortOption?: SearchSortOption;
		};
		response: SearchResults;
	};
	PostSearchAdvanced: {
		body: AdvancedSearchRequest;
		path: 'Search/advanced';
		response: SearchResults;
	};
	PostSearchProductArchive: {
		body: ProductArchiveFacetSelection[];
		path: 'Search/productarchive';
		queryParams: {
			categoryId?: CategoryId;
			offset?: number;
			pageSize?: number;
			searchString?: string;
			sortOption?: ProductArchiveSortOption;
		};
		response: ProductArchiveSearchResponse;
	};
	PostShareCart: {
		path: 'Wishlist/sharecart/{{cartId}}';
		pathParams: {
			cartId: CartId;
		};
		response: string;
	};
	PostShareWishlist: {
		path: 'Wishlist/share';
		response: string;
	};
	PostTrackEvent: {
		path: 'TrackEvent';
		queryParams: {
			eventType: 'ProductClickEvent';
			value: VariantId;
		};
		response: undefined;
	};
	PostUserFileStorage: {
		body: string;
		path: 'UserFileStorage/{{storageType}}';
		pathParams: {
			storageType: StorageType;
		};
		response: StoredFileResponse;
	};
	PostVerifyRedirectPayment: {
		body: VerifyPaymentRequest | undefined;
		path: 'Cart/{{cartId}}/verifyredirectpayment';
		pathParams: {
			cartId: CartId;
		};
		response: Cart;
	};
	PostWatchVariantStock: {
		path: 'VariantStock/watch';
		queryParams: {
			email: string;
			variantId: VariantId;
		};
		response: undefined;
	};
	PostWishlist: {
		path: 'Wishlist';
		queryParams?: {
			storeId?: StoreId;
		};
		response: Wishlist;
	};
	PostWishlistMiniVariant: {
		path: 'Wishlist/mini/variants/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		queryParams: {
			maxReturnedAccessories?: number;
			onlySellable?: 'false' | 'true';
			qty: number;
			returnAccessories?: 'false' | 'true';
			returnAddedProducts?: 'false' | 'true';
			setDistinctValue?: 'false' | 'true';
		};
		response: MinimizedAddToWishlistResponse;
	};
	PostWishlistMiniVariants: {
		body: VariantQtyBase[];
		path: 'Wishlist/mini/variants';
		queryParams?: {
			maxReturnedAccessories?: number;
			onlySellable?: 'false' | 'true';
			returnAccessories?: 'false' | 'true';
			returnAddedProducts?: 'false' | 'true';
			setDistinctValue?: 'false' | 'true';
		};
		response: MinimizedAddToWishlistResponse;
	};
	PostWishlistVariant: {
		path: 'Wishlist/variants/{{variantId}}';
		pathParams: {
			variantId: VariantId;
		};
		queryParams?: {
			qty: number;
			setDistinctValue?: 'false' | 'true';
			storeId?: StoreId;
		};
		response: Wishlist;
	};
	PostWishlistVariants: {
		body: VariantQtyBase[];
		path: 'Wishlist/variants';
		queryParams?: {
			setDistinctValue?: 'false' | 'true';
			storeId?: StoreId;
		};
		response: Wishlist;
	};
	PutCart: {
		body: CartRequest;
		path: 'Cart/{{cartId}}';
		pathParams: {
			cartId: CartId;
		};
		response: Cart;
	};
	PutContactAllowOnlinePurchase: {
		path: ContactPath;
		pathParams: ContactPathParams;
		queryParams: {
			allowOnlinePurchase: 'false' | 'true';
		};
		response: undefined;
	};
	PutCustomerJulaClub: {
		body: {
			address?: string;
			city?: string;
			coAddress?: string;
			email?: string;
			firstName?: string;
			lastName?: string;
			phoneNumber?: string;
			postalCode?: string;
		};
		path: 'Customer/julaclub';
		response: undefined;
	};
	PutCustomerOrderStatus: {
		path: 'Customer/{{customerId}}/order/{{orderId}}';
		pathParams: {
			customerId: string;
			orderId: TransactionId;
		};
		queryParams: {
			cancel: 'false' | 'true';
		};
		response: OrderDetail;
	};
	PutJulaProContact: {
		path: 'Customer/julapro/contact';
		queryParams: {
			allowMarketingCommunication: 'false' | 'true';
			email: string;
			mobileNumber: string;
		};
		response: undefined;
	};
	PutJulaProCustomer: {
		params: { body: JulaProCustomerUpdateRequest };
		path: 'Customer/julapro';
		response: undefined;
	};
	PutWishlistVariantPicked: {
		path: 'Wishlist/variants/{{variantId}}/pick';
		pathParams: {
			variantId: VariantId;
		};
		queryParams: {
			picked?: 'false' | 'true';
		};
		response: undefined;
	};
}

// prettier-ignore
export const dpApiEndpoints: EndpointDefinitions<DpApiEndpoints> = {
	DeleteContact: { allowEmpty: true, method: 'DELETE', path: 'Customer/julapro/contact/{{customerContactId}}' },
	DeleteWishlist: { allowEmpty: true, method: 'DELETE', path: 'Wishlist' },
	DeleteWishlistVariant: { allowEmpty: false, method: 'DELETE', path: 'Wishlist/variants/{{variantId}}' },
	GetBundleVariants: { allowEmpty: false, method: 'GET', path: 'Bundle/{{bundleId}}/variants' },
	GetCampaignProducts: { allowEmpty: false, method: 'GET', path: 'Campaign/{{campaignId}}' },
	GetCart: { allowEmpty: false, method: 'GET', path: 'Cart/{{cartId}}' },
	GetCartMini: { allowEmpty: false, method: 'GET', path: 'Cart/mini' },
	GetCommunicationPreferences: { allowEmpty: false, method: 'GET', path: 'Customer/julaclub/communication-preferences' },
	GetContact: { allowEmpty: false, method: 'GET', path: 'Customer/julapro/contact/{{customerContactId}}' },
	GetContacts: { allowEmpty: false, method: 'GET', path: 'Customer/julapro/contact' },
	GetCreditSimulation: { allowEmpty: false, method: 'GET', path: 'Customer/CreditSimulation' },
	GetCustomer: { allowEmpty: false, method: 'GET', path: 'Customer' },
	GetCustomerAvailability: { allowEmpty: false, method: 'GET', path: 'CustomerService/availability' },
	GetCustomerBonus: { allowEmpty: false, method: 'GET', path: 'Customer/bonus' },
	GetCustomerBonusChecks: { allowEmpty: false, method: 'GET', path: 'Customer/bonusChecks' },
	GetCustomerInvoices: { allowEmpty: false, method: 'GET', path: 'Customer/invoice' },
	GetCustomerOrder: { allowEmpty: false, method: 'GET', path: 'Customer/order/{{orderId}}' },
	GetCustomerOrders: { allowEmpty: false, method: 'GET', path: 'Customer/order' },
	GetCustomerOrderStatus: { allowEmpty: false, method: 'GET', path: 'Customer/{{customerId}}/order/{{orderId}}' },
	GetCustomerSearch: { allowEmpty: false, method: 'GET', path: 'Customer/search' },
	GetDeliveryMethods: { allowEmpty: false, method: 'GET', path: 'DeliveryMethods/variant/{{variantId}}' },
	GetFaqSearch: { allowEmpty: false, method: 'GET', path: 'FaqSearch/{{searchString}}' },
	GetJulaProSearchCompany: { allowEmpty: false, method: 'GET', path: 'Customer/julapro/search/company' },
	GetJulaProWorksite: { allowEmpty: false, method: 'GET', path: 'Customer/julapro/worksite/{{worksiteId}}' },
	GetNearbyStores: { allowEmpty: false, method: 'GET', path: 'Stores/nearbyStores' },
	GetOrder: { allowEmpty: false, method: 'GET', path: 'Customer/order' },
	GetPermissions: { allowEmpty: false, method: 'GET', path: 'Customer/permissions' },
	GetProductCustomization: { allowEmpty: false, method: 'GET', path: 'ProductCustomization/{{variantId}}' },
	GetProductQuestions: { allowEmpty: false, method: 'GET', path: 'ProductQuestions/{{variantId}}' },
	GetQuotation: { allowEmpty: false, method: 'GET', path: 'Quotations/{{quotationId}}' },
	GetQuotations: { allowEmpty: false, method: 'GET', path: 'Quotations' },
	GetRelatedProducts: { allowEmpty: false, method: 'GET', path: 'variant/{{variantId}}/relatedproducts' },
	GetResursPaymentPriceSignage: { allowEmpty: false, method: 'GET', path: 'payment/resursbank/price-signage' },
	GetReviews: { allowEmpty: false, method: 'GET', path: 'Reviews/{{variantId}}' },
	GetSearchPopularProducts: { allowEmpty: false, method: 'GET', path: 'Search/popularproducts' },
	GetSearchPopularTerms: { allowEmpty: false, method: 'GET', path: 'Search/popularterms' },
	GetSharedWishlist: { allowEmpty: false, method: 'GET', path: 'Wishlist/{{wishlistId}}' },
	GetSimilarProducts: { allowEmpty: false, method: 'GET', path: 'Product/{{productId}}/similar' },
	GetStore: { allowEmpty: false, method: 'GET', path: 'Stores/{{storeId}}' },
	GetStores: { allowEmpty: false, method: 'GET', path: 'Stores' },
	GetTicket: { allowEmpty: false, method: 'GET', path: 'Ticket/{{ticketType}}' },
	GetUserFileStorage: { allowEmpty: false, method: 'GET', path: 'UserFileStorage/{{storageType}}' },
	GetVariant: { allowEmpty: false, method: 'GET', path: 'variant/{{variantId}}' },
	GetVariantStock: { allowEmpty: false, method: 'GET', path: 'variantStock' },
	GetVariantStockStores: { allowEmpty: false, method: 'GET', path: 'variantStock/{{variantId}}/stores' },
	GetWishlistMini: { allowEmpty: false, method: 'GET', path: 'Wishlist/mini' },
	PostCart: { allowEmpty: false, method: 'POST', path: 'Cart' },
	PostCartFinalizePayment: { allowEmpty: false, method: 'POST', path: 'Cart/{{cartId}}/finalize' },
	PostCartMiniVariant: { allowEmpty: false, method: 'POST', path: 'Cart/mini/variants/{{variantId}}' },
	PostCartMiniVariants: { allowEmpty: false, method: 'POST', path: 'Cart/mini/variants' },
	PostCartPayment: { allowEmpty: false, method: 'POST', path: 'Cart/{{cartId}}/payment' },
	PostCartVariant: { allowEmpty: false, method: 'POST', path: 'Cart/variants/{{variantId}}' },
	PostCartVariants: { allowEmpty: false, method: 'POST', path: 'Cart/variants' },
	PostCheckoutCart: { allowEmpty: false, method: 'POST', path: 'Cart/checkout' },
	PostCommunicationPreferences: { allowEmpty: false, method: 'POST', path: 'Customer/julaclub/communication-preferences' },
	PostConsent: { allowEmpty: true, method: 'POST', path: 'Customer/julaclub/consent' },
	PostCopyCart: { allowEmpty: false, method: 'POST', path: 'Cart/{{cartId}}/copy' },
	PostFeedback: { allowEmpty: true, method: 'POST', path: 'Feedback' },
	PostProductCustomization: { allowEmpty: false, method: 'POST', path: 'ProductCustomization/{{variantId}}' },
	PostProductList: { allowEmpty: false, method: 'POST', path: 'ProductList' },
	PostQuotationPayment: { allowEmpty: false, method: 'POST', path: 'Quotations/{{quotationId}}/payment' },
	PostSearch: { allowEmpty: false, method: 'POST', path: 'Search' },
	PostSearchAdvanced: { allowEmpty: false, method: 'POST', path: 'Search/advanced' },
	PostSearchProductArchive: { allowEmpty: false, method: 'POST', path: 'Search/productarchive' },
	PostShareCart: { allowEmpty: false, method: 'POST', path: 'Wishlist/sharecart/{{cartId}}' },
	PostShareWishlist: { allowEmpty: false, method: 'POST', path: 'Wishlist/share' },
	PostTrackEvent: { allowEmpty: true, method: 'POST', path: 'TrackEvent' },
	PostUserFileStorage: { allowEmpty: false, method: 'POST', path: 'UserFileStorage/{{storageType}}' },
	PostVerifyRedirectPayment: { allowEmpty: false, method: 'POST', path: 'Cart/{{cartId}}/verifyredirectpayment' },
	PostWatchVariantStock: { allowEmpty: true, method: 'POST', path: 'VariantStock/watch' },
	PostWishlist: { allowEmpty: false, method: 'POST', path: 'Wishlist' },
	PostWishlistMiniVariant: { allowEmpty: false, method: 'POST', path: 'Wishlist/mini/variants/{{variantId}}' },
	PostWishlistMiniVariants: { allowEmpty: false, method: 'POST', path: 'Wishlist/mini/variants' },
	PostWishlistVariant: { allowEmpty: false, method: 'POST', path: 'Wishlist/variants/{{variantId}}' },
	PostWishlistVariants: { allowEmpty: false, method: 'POST', path: 'Wishlist/variants' },
	PutCart: { allowEmpty: false, method: 'PUT', path: 'Cart/{{cartId}}' },
	PutContactAllowOnlinePurchase: { allowEmpty: true, method: 'PUT', path: 'Customer/julapro/contact/{{customerContactId}}' },
	PutCustomerJulaClub: { allowEmpty: true, method: 'PUT', path: 'Customer/julaclub' },
	PutCustomerOrderStatus: { allowEmpty: false, method: 'PUT', path: 'Customer/{{customerId}}/order/{{orderId}}' },
	PutJulaProContact: { allowEmpty: true, method: 'PUT', path: 'Customer/julapro/contact' },
	PutJulaProCustomer: { allowEmpty: true, method: 'PUT', path: 'Customer/julapro' },
	PutWishlistVariantPicked: { allowEmpty: true, method: 'PUT', path: 'Wishlist/variants/{{variantId}}/pick' },
};
